/**
 * Idle-excluded active-time accumulator for a single in-progress annotation.
 *
 * The per-lesion "draw time" (start_ts → save) historically counted raw
 * wall-clock, so switching tabs or walking away inflated it. This hook tracks
 * the active engagement time within the annotation window: it sums the spans
 * between input events, dropping any inactivity gap longer than
 * `ANNOTATION_IDLE_THRESHOLD_MS` and any tab-hidden span entirely.
 *
 * Event-driven and ref-only — no React state, so the high-frequency mousemove
 * listener never triggers a re-render. The accumulator re-anchors whenever
 * `annotationStartTs` changes to a new value (new lesion, or Edit re-stamps it)
 * and resets when it goes null (save/clear).
 *
 * Consumers call `getActiveSeconds(endTs)` at save time to get the value to
 * persist as `timing.active_seconds`.
 */
import { useCallback, useEffect, useRef } from "react";

/**
 * Inactivity gap (ms) beyond which time is treated as idle and excluded.
 * Shared with the case-session timer so both annotation timers use one
 * definition of "idle". The study-mode reading timer keeps its own 30s default.
 */
export const ANNOTATION_IDLE_THRESHOLD_MS = 60_000;

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "wheel",
  "keydown",
  "click",
  "touchstart",
] as const;

export interface UseAnnotationActiveTimeResult {
  /**
   * Active (idle-excluded) seconds for the current annotation window.
   * Pass the save timestamp as `endTs` so the trailing span up to save is
   * counted (sub-threshold) or dropped (over-threshold) consistently.
   */
  getActiveSeconds: (endTs?: string) => number;
}

export function useAnnotationActiveTime(
  annotationStartTs: string | null,
): UseAnnotationActiveTimeResult {
  const activeMsRef = useRef(0);
  const lastInputAtRef = useRef(0);
  const anchoredStartRef = useRef<string | null>(null);
  const hiddenRef = useRef(false);

  // (Re)anchor the accumulator when the annotation window opens or restarts.
  useEffect(() => {
    if (!annotationStartTs) {
      anchoredStartRef.current = null;
      return;
    }
    if (anchoredStartRef.current === annotationStartTs) return;
    anchoredStartRef.current = annotationStartTs;
    activeMsRef.current = 0;
    const parsed = Date.parse(annotationStartTs);
    lastInputAtRef.current = Number.isNaN(parsed) ? Date.now() : parsed;
    hiddenRef.current = document.hidden;
  }, [annotationStartTs]);

  // Listen for activity + tab-visibility only while an annotation is in progress.
  useEffect(() => {
    if (!annotationStartTs) return;

    const handleInput = () => {
      const now = Date.now();
      if (hiddenRef.current) {
        // Defensive: input while flagged hidden → treat as resume, drop the gap.
        hiddenRef.current = false;
        lastInputAtRef.current = now;
        return;
      }
      const gap = now - lastInputAtRef.current;
      if (gap > 0 && gap <= ANNOTATION_IDLE_THRESHOLD_MS) {
        activeMsRef.current += gap;
      }
      // gap > threshold → idle, excluded entirely
      lastInputAtRef.current = now;
    };

    const handleVisibility = () => {
      const now = Date.now();
      if (document.hidden) {
        // Bank the active span up to now, then exclude the hidden span.
        const gap = now - lastInputAtRef.current;
        if (gap > 0 && gap <= ANNOTATION_IDLE_THRESHOLD_MS) {
          activeMsRef.current += gap;
        }
        lastInputAtRef.current = now;
        hiddenRef.current = true;
      } else {
        // Resume: skip the entire hidden span by re-anchoring to now.
        lastInputAtRef.current = now;
        hiddenRef.current = false;
      }
    };

    for (const ev of ACTIVITY_EVENTS) {
      // passive + capture mirrors useIdleTimer so scroll isn't slowed.
      document.addEventListener(ev, handleInput, { passive: true, capture: true });
    }
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        document.removeEventListener(ev, handleInput, { capture: true });
      }
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [annotationStartTs]);

  const getActiveSeconds = useCallback((endTs?: string) => {
    if (anchoredStartRef.current === null) return 0;
    const parsedEnd = endTs ? Date.parse(endTs) : Date.now();
    const endMs = Number.isNaN(parsedEnd) ? Date.now() : parsedEnd;
    let total = activeMsRef.current;
    if (!hiddenRef.current) {
      const tailGap = endMs - lastInputAtRef.current;
      if (tailGap > 0 && tailGap <= ANNOTATION_IDLE_THRESHOLD_MS) {
        total += tailGap;
      }
    }
    return Math.max(0, Math.round(total / 1000));
  }, []);

  return { getActiveSeconds };
}
